// src/pages/CreateEvents.jsx (Updated and Final)

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import AxiosInstance from "../context/axiosInstance"; 
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import EventListModal from "../pages/EventList";
import AppFooter from "./Homepage/AppFooter";
import ConflictWarningModal from "./ConflictWarningModal";
import moment from "moment";
import LocationPicker from "./LocationPicker"; 

import {
  ArrowLeft,
  X,
  Plus,
  Loader2,
  CalendarPlus,
  Users,
  MessageSquareQuote,
  Send,
  Lock,
  XCircle,
  UserCheck,
  UserPlus,
} from "lucide-react";

// --- Reusable Form Sub-Components ---
// These are well-structured, no changes needed.
const FormSection = ({ icon, title, children }) => (
  <fieldset className="border-t border-white/10 pt-6 mt-8">
    <legend className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2 uppercase tracking-wider">
      <span className="p-1.5 rounded-lg bg-white/5">{icon}</span> {title}
    </legend>
    {children}
  </fieldset>
);

const FormInput = React.forwardRef((props, ref) => (
  <input
    ref={ref}
    {...props}
    className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all hover:bg-white/10 placeholder-gray-500"
  />
));

// --- Main Page Component ---
const CreateEvents = () => {
  const navigate = useNavigate();
  // ✅ Get userID from the global context
  const { userID } = useAppContext();
  const [conflictData, setConflictData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  // --- State Management ---
  const [eventData, setEventData] = useState({
    eventName: "",
    eventDate: "",
    eventTime: "",
    venue: "",
    description: "",
    inviteExpiryDate: "",
  });
  const [invitees, setInvitees] = useState([]);
  const [inviteInput, setInviteInput] = useState("");
  const [coHosts, setCoHosts] = useState([]);
  const [coHostInput, setCoHostInput] = useState("");
  const [isVerifyingCoHost, setIsVerifyingCoHost] = useState(false);
  const [rsvpQuestions, setRsvpQuestions] = useState([
    {
      questionId: "q_attending",
      questionText: "Will you be able to attend?",
      type: "boolean",
      required: true,
    },
  ]);
  const [newQuestionText, setNewQuestionText] = useState("");
  const [newQuestionType, setNewQuestionType] = useState("text");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEventListModalOpen, setEventListModalOpen] = useState(false);
  const [location, setLocation] = useState(null); 

  // --- Handlers for Form State ---
  const handleChange = (e) =>
    setEventData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const addInvitee = () => {
    if (!/^\+\d{8,15}$/.test(inviteInput))
      return toast.error("Invalid phone number format.");
    if (invitees.includes(inviteInput))
      return toast.error("Number already added to invitees.");
    setInvitees([...invitees, inviteInput]);
    setInviteInput("");
  };
  const removeInvitee = (numToRemove) =>
    setInvitees(invitees.filter((num) => num !== numToRemove));
  const removeCoHost = (userIdToRemove) =>
    setCoHosts(coHosts.filter((ch) => ch.user_id !== userIdToRemove));
  const addRsvpQuestion = () => {
    const trimmed = newQuestionText.trim();
    if (!trimmed) return;
    const id = `q_custom_${Date.now()}`;
    setRsvpQuestions([
      ...rsvpQuestions,
      {
        questionId: id,
        questionText: trimmed,
        type: newQuestionType,
        required: false,
      },
    ]);
    setNewQuestionText("");
    setNewQuestionType("text");
  };
  const removeRsvpQuestion = (id) => {
    if (id === "q_attending") return;
    setRsvpQuestions((prev) => prev.filter((q) => q.questionId !== id));
  };

  // ✅ Co-host validation using the global AxiosInstance instance
  const validateAndAddCoHost = async () => {
    if (!/^\+\d{8,15}$/.test(coHostInput))
      return toast.error("Invalid phone number format.");

    setIsVerifyingCoHost(true);
    try {
      const { data } = await AxiosInstance.post("/auth/contacts/validate", {
        contacts: [coHostInput],
      });

      const validUsers = data.valid_users || [];
      if (validUsers.length > 0) {
        const user = validUsers[0];
        if (coHosts.some((ch) => ch.user_id === user.user_id)) {
          toast.error("This user is already a co-host.");
        } else {
          setCoHosts([...coHosts, user]);
          setCoHostInput("");
          toast.success(`Added ${user.full_name} as co-host`);
        }
      } else {
        toast.error("This phone number does not belong to a registered user.");
      }
    } catch (error) {
      console.error("Error validating co-host:", error);
      toast.error(error.response?.data?.error || "Failed to validate co-host.");
    } finally {
      setIsVerifyingCoHost(false);
    }
  };
const handleCreateAttempt = async (e) => {
  e.preventDefault();
  if (!userID) return toast.error("Please log in again.");
  if (!eventData.eventDate || !eventData.eventTime)
    return toast.error("Please set a date and time.");

  setIsSubmitting(true);
  try {
    // 1. Construct timestamps for checking
    const startTime = new Date(
      `${eventData.eventDate}T${eventData.eventTime}`
    ).toISOString();
    // Assume a 1-hour duration for checking if no end time is specified
    const endTime = moment(startTime).add(1, "hour").toISOString();
console.log("Checking conflicts for:", { startTime, endTime });
    // 2. Call the conflict checker
    const { data: conflictCheckData } = await AxiosInstance.post(
      "/timeline/conflicts",
      {
        start_time: startTime,
        end_time: endTime,
      }
    );

    // ✅ Add explicit status check
    if (conflictCheckData.has_conflict) {
      // 3a. Conflict found, show the warning modal
      setConflictData(conflictCheckData);
      setShowConflictModal(true);
    } else {
      // 3b. No conflict, proceed immediately
      await handleSubmit();
    }
  } catch (err) {
    console.error("Conflict check error:", err);
    console.error("Response data:", err.response?.data);
    toast.error(err.response?.data?.error || "Could not check for conflicts.");
  } finally {
    setIsSubmitting(false);
  }
};

const handleSubmit = async (e = null) => {
  if (e) e.preventDefault();
  if (!userID) return toast.error("User not identified. Please log in again.");
    if (!location || !location.address) {
      return toast.error("Please select a venue for the event.");
    }
  const fullEventData = {
    eventData: {
      eventName: eventData.eventName,
      eventDateTime: new Date(
        `${eventData.eventDate}T${eventData.eventTime}`
      ).toISOString(),
      venue: {
        address: location.address,
        latitude: location.lat,
        longitude: location.lng,
      },
      description: eventData.description,
      inviteExpiryDate: new Date(eventData.inviteExpiryDate)
        .toISOString()
        .split("T")[0],
      rsvpQuestionnaire: rsvpQuestions,
    },
    invitees,
    host_id: userID,
    coHosts: coHosts, // Send only the user_ids
  };

  setIsSubmitting(true);
  toast
    .promise(AxiosInstance.post("/events/", fullEventData), {
      loading: "Creating your event...",
      success: (res) => {
        // Reset form on success
        setEventData({
          eventName: "",
          eventDate: "",
          eventTime: "",
          venue: "",
          description: "",
          inviteExpiryDate: "",
        });
        setInvitees([]);
        setCoHosts([]);
        setRsvpQuestions([
          {
            questionId: "q_attending",
            questionText: "Will you be able to attend?",
            type: "boolean",
            required: true,
          },
        ]);
        return res.data.message || "Event created successfully!";
      },
      error: (err) => err.response?.data?.error || "Failed to create event.",
    })
    .finally(() => {
      setIsSubmitting(false);
    });
};


  return (
    <>
      <div className="bg-background text-white min-h-screen font-sans selection:bg-white selection:text-black">
        {/* Background Gradient */}
        <div className="fixed inset-0 pointer-events-none">
           <div className="absolute top-[5%] left-[5%] w-[500px] h-[500px] bg-primary-500/10 rounded-full blur-[120px]" />
           <div className="absolute bottom-[5%] right-[5%] w-[600px] h-[600px] bg-secondary-500/10 rounded-full blur-[120px]" />
        </div>

        <main className="flex-grow relative z-10">
          <div className="container mx-auto px-6 py-24 md:py-32">
            {/* --- Page Header --- */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <button
                onClick={() => navigate("/app")}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-widest mb-8"
              >
                <ArrowLeft size={16} /> Back to Dashboard
              </button>
            </motion.div>

            <div className="grid lg:grid-cols-5 gap-16">
              <motion.div
                className="lg:col-span-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                {" "}
                <h1 className="text-3xl md:text-5xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400 mb-6">
                  Plan Something Memorable
                </h1>
                <p className="text-xl text-gray-400 mt-4 leading-relaxed font-light">
                  Invite friends, organize meetups, or host a public event – it
                  all starts here!
                </p>
                <button
                  onClick={() => setEventListModalOpen(true)}
                  className="mt-8 inline-flex items-center gap-2 text-primary-400 font-semibold hover:text-primary-300 transition-colors bg-primary-500/10 px-6 py-3 rounded-full border border-primary-500/20"
                >
                  View My Created Events
                </button>
              </motion.div>

              <motion.div
                className="lg:col-span-3 bg-surface/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                {" "}
                <form onSubmit={handleCreateAttempt}>
                  <h2 className="text-2xl font-bold mb-6 text-white border-l-4 border-primary-500 pl-4">
                    Host a New Experience
                  </h2>

                  {/* Event Details Section */}
                  <FormSection
                    icon={<CalendarPlus size={16} />}
                    title="Event Details"
                  >
                    <div className="space-y-4">
                      <FormInput
                        name="eventName"
                        placeholder="Event Name"
                        value={eventData.eventName}
                        onChange={handleChange}
                        required
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <FormInput
                          name="eventDate"
                          type="date"
                          value={eventData.eventDate}
                          onChange={handleChange}
                          required
                        />
                        <FormInput
                          name="eventTime"
                          type="time"
                          value={eventData.eventTime}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <LocationPicker onLocationSelect={setLocation} />
                      <textarea
                        name="description"
                        placeholder="Describe your event..."
                        value={eventData.description}
                        onChange={handleChange}
                        required
                        rows={3}
                        className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all placeholder-gray-500"
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Invite Response Deadline
                        </label>
                        <FormInput
                          name="inviteExpiryDate"
                          type="date"
                          value={eventData.inviteExpiryDate}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </FormSection>

                  {/* Co-Hosts Section - Now using react-international-phone */}
                  <FormSection
                    icon={<UserPlus size={16} />}
                    title="Add Co-Hosts"
                  >
                    <div className="bg-white/5 border border-white/10 p-4 rounded-xl space-y-4">
                      <p className="text-sm text-gray-400">
                        Co-hosts can help manage the event and invite guests.
                      </p>
                      <div className="flex gap-2">
                        <div className="flex-grow">
                          <PhoneInput
                            defaultCountry="in"
                            value={coHostInput}
                            onChange={(phone) => setCoHostInput(phone)}
                            inputClassName="!w-full !bg-transparent !border-none !text-white focus:!ring-0 !text-base"
                            className="flex items-center w-full bg-white/5 border border-white/10 rounded-xl px-4 text-white focus-within:ring-2 focus-within:ring-primary-500 transition-all hover:bg-white/10"
                            countrySelectorStyleProps={{
                              buttonClassName: "!bg-transparent !border-none !pr-2",
                              dropdownClassName: "!bg-surface !border-white/10 !text-black"
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={validateAndAddCoHost}
                          disabled={
                            isVerifyingCoHost ||
                            !/^\+\d{8,15}$/.test(coHostInput)
                          }
                          className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-xl font-semibold transition-colors flex items-center gap-2 whitespace-nowrap text-white shadow-lg shadow-primary-500/20"
                        >
                          {isVerifyingCoHost ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <UserCheck size={16} />
                          )}
                          Verify
                        </button>
                      </div>
                      {coHosts.length > 0 && (
                        <div className="space-y-2 pt-2">
                          <p className="text-sm font-medium text-gray-300">
                            Added Co-Hosts:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {coHosts.map((coHost) => (
                              <span
                                key={coHost.user_id}
                                className="flex items-center gap-2 bg-purple-500/20 text-purple-300 px-3 py-1.5 rounded-lg border border-purple-500/30 font-medium text-sm"
                              >
                                <span>
                                  {coHost.full_name || "Unknown"}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeCoHost(coHost.user_id)}
                                  className="hover:text-white transition-colors"
                                >
                                  <X size={14} />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </FormSection>
                  <FormSection icon={<Users size={16} />} title="Invite Guests">
                    <div className="bg-white/5 border border-white/10 p-4 rounded-xl">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {invitees.map((num) => (
                          <span
                            key={num}
                            className="flex items-center gap-2 bg-primary-500/20 text-primary-300 text-xs font-medium px-2.5 py-1 rounded-lg border border-primary-500/20"
                          >
                            {num}{" "}
                            <button
                              type="button"
                              onClick={() => removeInvitee(num)}
                              className="hover:text-white"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                      <PhoneInput
                        defaultCountry="in"
                        value={inviteInput}
                        onChange={(phone) => setInviteInput(phone)}
                        inputProps={{
                          onKeyDown: (e) => {
                            if (e.key === "Enter" || e.key === ",") {
                              e.preventDefault();
                              addInvitee();
                            }
                          },
                        }}
                        inputClassName="!w-full !bg-transparent !border-none !text-white focus:!ring-0 !text-base !p-0"
                        className="flex items-center w-full bg-transparent px-2"
                        countrySelectorStyleProps={{
                              buttonClassName: "!bg-transparent !border-none !pr-2",
                              dropdownClassName: "!bg-surface !border-white/10 !text-black"
                            }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 ml-1">
                      Enter a phone number and press <span className="text-gray-300 font-medium">Enter</span> to add.
                    </p>
                  </FormSection>

                  <FormSection
                    icon={<MessageSquareQuote size={16} />}
                    title="RSVP Form"
                  >
                    <div className="space-y-2 mb-4">
                      {rsvpQuestions.map((q) => (
                        <div
                          key={q.questionId}
                          className="flex justify-between items-center bg-white/5 border border-white/5 p-3 rounded-xl text-sm"
                        >
                          {q.questionId === "q_attending" ? (
                            <Lock
                              size={16}
                              className="text-gray-500 flex-shrink-0"
                            />
                          ) : (
                            // The delete button for custom questions
                            <button
                              type="button"
                              onClick={() => removeRsvpQuestion(q.questionId)}
                              className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
                              aria-label="Remove question"
                            >
                              <XCircle size={18} />
                            </button>
                          )}

                          <span className="text-gray-300 font-medium ml-2 mr-auto">
                            {q.questionText}
                          </span>
                          <span className="text-xs font-mono py-1 px-2 bg-white/10 rounded text-gray-400 uppercase tracking-wide">
                            {q.type}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="grid sm:grid-cols-3 gap-2">
                      <FormInput
                        placeholder="New question..."
                        value={newQuestionText}
                        onChange={(e) => setNewQuestionText(e.target.value)}
                        className="sm:col-span-2"
                      />
                      <select
                        value={newQuestionType}
                        onChange={(e) => setNewQuestionType(e.target.value)}
                        className="h-12 bg-white/5 border border-white/10 rounded-xl px-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none transition-all hover:bg-white/10"
                      >
                        <option className="text-black" value="text">
                          Text
                        </option>
                        <option className="text-black" value="number">
                          Number
                        </option>
                        <option className="text-black" value="boolean">
                          Yes/No
                        </option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={addRsvpQuestion}
                      className="w-full text-sm mt-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center gap-2 font-medium text-gray-300"
                    >
                      <Plus size={16} /> Add Question
                    </button>
                  </FormSection>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full mt-8 bg-white text-black font-bold py-4 rounded-xl text-lg hover:bg-gray-100 transition-all flex items-center justify-center gap-2 disabled:opacity-50 hover:shadow-lg hover:scale-[1.02]"
                  >
                    {isSubmitting ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Send size={18} />
                    )}
                    {isSubmitting
                      ? "Creating Event..."
                      : "Create & Send Invites"}
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
        </main>
        <div className="container mx-auto px-6">
          <AppFooter />
        </div>
      </div>
      <EventListModal
        isOpen={isEventListModalOpen}
        onClose={() => setEventListModalOpen(false)}
      />
      <AnimatePresence>
        {showConflictModal && (
          <ConflictWarningModal
            newActionTitle={eventData.eventName}
            conflicts={conflictData.conflicting_items}
            onConfirmOverride={async () => {
              setShowConflictModal(false);
              setIsSubmitting(true);
              await handleSubmit();
              setIsSubmitting(false);
            }}
            onCancel={() => setShowConflictModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default CreateEvents;
